/**
 * SOLID Review
 * S — Strong. One concern: whether this page load sends an error it caught — the error as a `ClientError`, the page
 *     load's caps as an `ErrorLedger`, answered through a `ClientDecision`.
 * O — Strong. The caps are constants of the schema; nothing here changes with them.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. One role, `IServiceDecisionTable`: the Facade sees the diagnostics op's table and nothing else.
 * D — Strong. The error and its ledger, the decision builder and the schemas.
 */
import { ClientError } from '../../diagnostics/ClientError';
import { ErrorLedger } from '../../diagnostics/ErrorLedger';
import { ClientDecision, type TClientDecision } from '../ClientDecision';
import type { IServiceDecisionTable, TDecisionRows } from '../IServiceDecisionTable';
import type { IPropsClientInput } from '../../ports/schemas/ClientChanges';
import type { ICommandClientError, IEntityClientErrorPlan } from '../../ports/schemas/DiagnosticsMoves';

/** The ops this service answers for on the client Domain's lollipop. */
type TDiagnosticsOp = 'planClientError';

/**
 * CLIENT ERROR REPORTING (frontend-domain-oo P6, 2026-10-26; handbook §28.273) — the decide function of
 * `decisions/diagnostics/clientError.ts` (plan 2026-10-09, EP5) as a service.
 */
export class Diagnostics implements IServiceDecisionTable<TDiagnosticsOp> {
  /** Its decisions, by op: what `ClientDecisions` merges and answers through. */
  readonly decisions: TDecisionRows<TDiagnosticsOp> = {
    planClientError: (input, command) => this.planClientError(input, command),
  };

  /** An error the Framework caught: the report to send, if the page load's caps allow it. */
  planClientError(input: IPropsClientInput, command: ICommandClientError): TClientDecision<IEntityClientErrorPlan> {
    const error = ClientError.of(command);
    const ledger = ErrorLedger.of(input.session.diagnostics);
    const plan: IEntityClientErrorPlan = { report: ledger.sent(error, command.now) ? error.report() : null };
    return ClientDecision.keeping(input, ledger).answering(plan);
  }
}
